/**
 * Shared :focus-visible outline injector for web components (ADR-025 Rule 2).
 *
 * Injects a single <style> element (idempotent per id) that suppresses the
 * default :focus outline and draws the design-system focus ring on
 * :focus-visible for the given selectors. Web-only — imported explicitly from
 * `.web.tsx` implementations.
 */

/// <reference lib="dom" />

import { focusRing } from './focusRing';

/** The design-system focus ring as CSS declarations, for hand-written rules. */
export const FOCUS_RING_OUTLINE =
  `outline: ${focusRing.width}px solid ${focusRing.color};` +
  ` outline-offset: ${focusRing.offset}px;`;

/** Injects one `<style>` element per id (idempotent); a no-op outside the DOM. */
export function ensureStyle(id: string, css: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

export function ensureFocusVisibleStyle(id: string, selectors: string[]): void {
  ensureStyle(
    id,
    [
      ...selectors.map((selector) => `${selector}:focus { outline: none; }`),
      ...selectors.map((selector) => `${selector}:focus-visible { ${FOCUS_RING_OUTLINE} }`),
    ].join('\n'),
  );
}
